import { CircularProgress, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import moment from "moment";
import React, { useState } from "react";
import BigCalendar from "react-big-calendar-like-google";
import "react-big-calendar-like-google/lib/css/react-big-calendar.css";
import { useTranslation } from "react-i18next";
import {
  FaDog,
  FaExternalLinkAlt,
  FaInfoCircle,
  FaStickyNote,
  FaUser
} from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { useNavigate, useSearchParams } from "react-router-dom";
import SimpleIconAlert from "../../../components/display/SimpleIconAlert";
import UserHint from "../../../components/display/UserHint";
import BookingModal from "../../../components/modals/bookingModalComponents/components/BookingModal";
import BookingModalContext from "../../../components/modals/bookingModalComponents/context/BookingModalContext";
import CustomDrawer from "../../../components/wrappers/CustomDrawer";
import { ToggleDrawerContext } from "../../../contexts/ToggleDrawer";
import "../../../css/calendar.css";
import { STATUSES } from "../../../enums/AppointmentStatus";
import { CALENDAR_VIEW } from "../../../enums/LocalStorageValues";
import { WORKING_DAYS } from "../../../enums/QueryKeys";
import { APPOINTEMENT_DETAILS } from "../../../enums/UrlPaths";
import { fetchWorkingDays } from "../../../enums/queryFunction/Fetch";
import useUser from "../../../hooks/useUser";
import { getDateObject } from "../../../utils/DateTimeRepository";
import { isWorkingDay } from "../../appointments/utils/WorkingHoursRepositroy";
import useGetHomePageAdminAppointments, {
  useGetHomePageAdminAppointmentsMonth,
} from "../hooks/useGetHomePageAdminAppointments";

moment.updateLocale("en-gb", { week: { dow: 1 } });

BigCalendar.setLocalizer(BigCalendar.momentLocalizer(moment));

function parseCalendarAppointments(booking, service) {
  if (booking.startDate !== null) {
    return {
      id: booking.appointmentId,
      pet: booking?.petName,
      title: service,
      start: dayjs(getDateObject(booking?.startDate)).toDate(),
      end: dayjs(getDateObject(booking?.endDate)).toDate(),
      type: service,
      desc: booking.description,
      status: booking.status,
      pendingMessage:
        String(booking?.status).toUpperCase() == STATUSES.WAITING_CLINIC ||
        String(booking?.status).toUpperCase() == STATUSES.WAITING_USER
          ? "AppointmentNeedsToBeConfirmed"
          : null,
      owner: booking?.owner,
      ownerName: booking?.ownerName,
    };
  }
}

function serviceTranslation(service, currentLang = "en") {
  if (service?.translations?.[currentLang]) {
    return service.translations[currentLang];
  }
  return service?.title || "";
}

function getCalendarAppointments(data, lang) {
  if (!data || data == undefined) {
    return new Array();
  }

  return data.map((booking) =>
    parseCalendarAppointments(
      booking,
      serviceTranslation(booking?.priceList, lang),
    ),
  );
}

function getDaysWithAppointments(appointments) {
  if (appointments) {
    return appointments?.map((appointment) =>
      dayjs(appointment).format("DD/MM/YYYY"),
    );
  }
  return new Array();
}

export default function DesktopAppointement({ showTitle = true }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramDate = searchParams.get("date");

  const parsedDate = paramDate
    ? dayjs(paramDate, "DD/MM/YYYY", true).isValid()
      ? dayjs(paramDate, "DD/MM/YYYY").toDate()
      : new Date()
    : new Date();

  const [date, setDate] = useState(parsedDate);
  const [selectedDate, setSelectedDate] = React.useState(null);

  const handleDateChange = (newDate) => {
    setDate(newDate);
    searchParams.set("date", dayjs(newDate).format("DD/MM/YYYY"));
    setSearchParams(searchParams);
  };
  const { t } = useTranslation();

  var isToday = require("dayjs/plugin/isToday");
  dayjs.extend(isToday);

  React.useEffect(() => {
    const paramDate = searchParams.get("date");
    if (paramDate && dayjs(paramDate, "DD/MM/YYYY", true).isValid()) {
      const parsed = dayjs(paramDate, "DD/MM/YYYY").toDate();
      if (parsed.toDateString() !== date.toDateString()) {
        setDate(parsed);
      }
    }
  }, [searchParams]);

  //Add ${mode === "dark" ? 'dark-mode' : ''} to class name of calendar-container for dark mode (not done yet)
  return (
    <>
      <Stack>
        {showTitle ? (
          <Typography variant="h6">{t("AppointmentsCalendar")}</Typography>
        ) : null}
        <div className={`calendar-container`}>
          <MonthCalendar
            date={date}
            handleDateChange={(e) => handleDateChange(e)}
          />
          <WeekCalendar
            setSearchParams={setSearchParams}
            searchParams={searchParams}
            setSelectedDate={setSelectedDate}
            date={date}
            handleDateChange={(e) => handleDateChange(e)}
          />
        </div>
      </Stack>
      {selectedDate ? (
        <CustomDrawer padding={0} maxWidth="sx">
          <BookingModalContext key={"calendar-booking-context"}>
            <BookingModal
              selectedDate={selectedDate}
              key={"calendar-booking-modal"}
            />
          </BookingModalContext>
        </CustomDrawer>
      ) : null}
    </>
  );
}

function WeekCalendar({
  handleDateChange,
  date,
  searchParams,
  setSearchParams,
  setSelectedDate,
}) {
  const { i18n } = useTranslation();
  const { data, isLoading, error } = useGetHomePageAdminAppointments();

  const currentLang = i18n.language;

  const [view, setView] = React.useState(
    localStorage.getItem(CALENDAR_VIEW) ?? "agenda",
  );
  const [isOpen, setIsOpen] = React.useContext(ToggleDrawerContext);
  const { currentClinic, roles } = useUser();
  const { data: workingDays } = useQuery({
    queryKey: [WORKING_DAYS],
    queryFn: () => fetchWorkingDays(currentClinic?.id),
    staleTime: Infinity,
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSelectedDate = (e) => {
    const today = dayjs(e).isToday();
    const future = dayjs().isBefore(dayjs(e));
    if (future || today) {
      handleDateChange(e);
      if (!isWorkingDay(dayjs(e), workingDays, roles.adminOrEmployee)) {
        setSelectedDate(e);
        setIsOpen(isOpen ? false : true);
      }
    }
  };

  const appointments = getCalendarAppointments(data, currentLang);

  function handleNavigate(id) {
    navigate(APPOINTEMENT_DETAILS + id);
  }
  const formats = {
    timeGutterFormat: (date, culture, localizer) =>
      localizer.format(date, "HH:mm", culture),
    dayFormat: (date, culture, localizer) =>
      localizer.format(date, "DD/MM/YYYY", culture),
    weekdayFormat: (date, culture, localizer) =>
      localizer.format(date, "DD/MM", culture),
    monthHeaderFormat: (date, culture, localizer) =>
      localizer.format(date, "MMMM YYYY", culture),
    dayHeaderFormat: (date, culture, localizer) =>
      localizer.format(date, "dddd, DD/MM/YYYY", culture),
    agendaHeaderFormat: () => moment(date).format("DD/MM/YYYY"),
  };

  function getFormat() {
    if (roles.user) {
      delete formats.agendaHeaderFormat;
    }
    return formats;
  }

  /**
   * React big calendar needs an object with a start and end date in order to read any value
   * Passing null, undefined, an empty array or empty object results in it not reading any value
   * We decided to simply pass in a dummy appointment if there are no real appointments from the backend
   * in order to display user a message
   */
  function createNoAppointmentsEvent(anchorDate) {
    const start = dayjs(anchorDate).startOf("day").toDate();
    //if end isn't in the same day as start "No Appointment" message will be shown 30 times
    const end = dayjs(anchorDate).endOf("day").toDate();
    const noAppointmentTitle = roles.user
      ? "NoAppointmentBetweenDates"
      : "NoAppointmentOnDate";
    return {
      noData: true,
      title: t(noAppointmentTitle),
      start,
      end,
    };
  }

  const CustomAgendaEvent = ({ event }) => {

    if (Boolean(event.noData)) {
      return <div sx={{ textAlign: "left" }}>{event.title}</div>;
    }
    
    return (
      <div className="agenda-event-wrapper">
        {/* Status Badge + Details Icon */}
        <div className="header-row">
          <div className={`status-badge ${String(event.status).toLowerCase()}`}>
            {t(event.status)}
          </div>
          <FaExternalLinkAlt
            className="details-icon"
            title={t("Details")}
            onClick={() => handleNavigate(event?.id)}
          />
        </div>
        {roles.user ? (
          <div className="header-row">
            <UserHint hint={event.pendingMessage} />
          </div>
        ) : null}

        {/* Pet */}
        {event.pet && (
          <div className="event-item">
            <FaDog className="event-icon" />
            <span>
              {t("Pet")}: <strong>{event.pet}</strong>
            </span>
          </div>
        )}

        {event.ownerName && (
          <div className="event-item">
            <FaUser className="event-icon" />
            <span>
              {t("Owner")}: <strong>{event.ownerName}</strong>
            </span>
          </div>
        )}

        {/* Owner */}
        {event.owner && (
          <div className="event-item">
            <MdEmail className="event-icon" />
            <span>
              {t("Email")}: <strong>{event.owner}</strong>
            </span>
          </div>
        )}

        {/* Service */}
        <div className="event-item">
          <FaInfoCircle className="event-icon" />
          <span>
            {t("Service")}: <strong>{event.title}</strong>
          </span>
        </div>

        {/* Description */}
        {event.desc && (
          <>
            <div className="event-item">
              <FaStickyNote className="event-icon" />
              <span>{t("Description")}:</span>
            </div>
            <p className="event-desc">{event.desc}</p>
          </>
        )}
      </div>
    );
  };

  const CustomAgendaTime = ({ event }) => {
    if (event && Boolean(event.noData)) {
      if (roles.user) {
        const rangeEnd = dayjs(event.startDate).add(30, "day").toDate();
        const endStr = moment(rangeEnd).format("MM/DD/YYYY");
        return (
          <>
            <div>
              {moment(event.start).format("DD")}{" "}
              {String(moment(event.start).format("MMM")).toUpperCase()}
              {" — "}
              {moment(endStr).format("DD")}{" "}
              {String(moment(endStr).format("MMM")).toUpperCase()}
            </div>
          </>
        );
      } else {
        return (
          <>
            <div>
              {moment(event.start).format("DD")}{" "}
              {String(moment(event.start).format("MMM")).toUpperCase()}
            </div>
          </>
        );
      }
    }
    return (
      <>
        <div>
          <div>
            {moment(event.start).format("DD")}{" "}
            {String(moment(event.start).format("MMM")).toUpperCase()}
          </div>
        </div>
        <div>
          <span>
            {moment(event.start).format("HH:mm")} -{" "}
            {moment(event.end).format("HH:mm")}
          </span>
        </div>
      </>
    );
  };

  const EmptyAllDayRow = () => null;

  function handleViewChange(view) {
    localStorage.setItem(CALENDAR_VIEW, String(view));
    setView(String(view));
    searchParams.set("view", String(view));
    setSearchParams(searchParams);
  }

  function getAppointments() {
    if (view !== String("agenda")) {
      return appointments;
    }
    if (roles.user) {
      return appointments.length
        ? appointments
        : [createNoAppointmentsEvent(date)];
    }

    const dayAppointments = appointments.filter((app) => {
      if (app && app.start) {
        return moment(app.start).isSame(moment(date), "day"); // Ensure correct type & granularity
      }
      return false;
    });

    if (!dayAppointments.length) {
      return [createNoAppointmentsEvent(date)];
    }
    return dayAppointments;
  }

  if (isLoading) {
    return (
      <Stack
        direction={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"650px"}
        width={"100%"}
      >
        <CircularProgress />
      </Stack>
    );
  }
  if (error) {
    return (
      <SimpleIconAlert color="black" message={"ErrorFetchingAppointments"} />
    );
  }
  return (
    <div className="week-calendar">
      <BigCalendar
        defaultView={view}
        views={["week", "day", "agenda"]}
        date={date}
        allDayAccessor={() => false}
        onView={handleViewChange}
        events={getAppointments()}
        startAccessor="start"
        endAccessor="end"
        step={30}
        timeslots={2}
        onNavigate={handleDateChange}
        onSelectEvent={(event) => {
          navigate(APPOINTEMENT_DETAILS + event?.id);
        }}
        selectable={true}
        onSelectSlot={(data) => {
          handleSelectedDate(data.start);
        }}
        components={{
          agenda: {
            date: () => null,
            time: CustomAgendaTime,
            event: CustomAgendaEvent,
            timeGutterHeader: EmptyAllDayRow,
            allDayEventWrapper: EmptyAllDayRow,
          },
        }}
        eventPropGetter={(event) => {
          if (Boolean(event.noData)) {
            return {
              style: {
                borderRadius: "0px",
                height: "12rem",
              },
            };
          }
          const currentView = localStorage.getItem(CALENDAR_VIEW) || "agenda";
          if (currentView === "week" || currentView === "day") {
            const backgroundColor =
              String(event.status).toUpperCase() ===
                String(STATUSES.WAITING_CLINIC) ||
              String(event.status).toUpperCase() ===
                String(STATUSES.WAITING_USER)
                ? "orange"
                : "#007bff";

            return {
              style: {
                backgroundColor,
                borderRadius: "0px",
              },
            };
          }
          return {};
        }}
        scrollToTime={new Date(1972, 0, 1, 7)}
        onSelecting={(slot) => false}
        formats={getFormat()}
        messages={{
          allDay: t("AllDay"),
          time: t("Time"),
          event: t("Appointments"),
          next: t("Next"),
          previous: t("Previous"),
          today: t("Today"),
          month: t("Month"),
          week: t("week"),
          day: t("day"),
          agenda: t("agenda"),
          date: t("Date"),
        }}
      />
    </div>
  );
}

function MonthCalendar({ handleDateChange, date }) {
  const { t } = useTranslation();
  const {
    data: monthCalendar,
    isLoading: loadingMonthCalendar,
    error: errorMonthCalendar,
  } = useGetHomePageAdminAppointmentsMonth();

  const daysWithAppointment = getDaysWithAppointments(monthCalendar);

  const customEvent = ({ value, children }) => {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        {children}
        {daysWithAppointment?.includes(dayjs(value).format("DD/MM/YYYY")) && (
          <div
            style={{
              position: "absolute",
              bottom: "20%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "6px",
              height: "6px",
              backgroundColor: "red",
              borderRadius: "50%",
              zIndex: 99,
            }}
          />
        )}
      </div>
    );
  };

  if (loadingMonthCalendar) {
    return (
      <Stack
        direction={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"650px"}
        width={"100%"}
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (errorMonthCalendar) {
    return (
      <SimpleIconAlert color="black" message={"ErrorFetchingAppointments"} />
    );
  }

  return (
    <div className="month-calendar">
      <BigCalendar
        defaultView="month"
        views={["month"]}
        date={date}
        selectable
        onSelectSlot={(data) => {
          handleDateChange(data.start);
        }}
        onNavigate={handleDateChange}
        components={{
          dateCellWrapper: customEvent,
        }}
        events={[]}
        messages={{
          allDay: t("AllDay"),
          time: t("Time"),
          event: t("Appointments"),
          next: t("Next"),
          previous: t("Previous"),
          today: t("Today"),
          month: t("Month"),
          week: t("week"),
          day: t("day"),
          agenda: t("agenda"),
          date: t("Date"),
        }}
      />
    </div>
  );
}
